import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import { ProductMasterDataExportConfigurationContext } from '../../../../../../../context/ProductMasterDataExportConfigurationContext';
import {
  ProductMasterDataExportConfiguration,
  ProductMasterDataExportConfigurationField,
  ProductMasterDataExportConfigurationInput,
} from '../../../../../../../types/product_master_data_export_configuration';
import { ISO_LanguageCode } from '../../../../../../../types/translation/localization.utils';
import { TranslationLanguage } from '../../../../../../../types/translation/translation';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { ProductMasterDataExportConfigurationRequest } from '../../../../../../../requests/productMasterDataExportConfiguration.request';
import { DraggableProductMasterDataExportFieldIdentifier } from '../Common/ProductMasterDataExportConfigurationForm';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import { Routing } from '../../../../../../../util/routes.util';
import { BackButton, ButtonVariant, CreateButton } from '../../../../../../components/Button';
import ProductMasterDataExportConfigurationForm from '../Common/ProductMasterDataExportConfigurationForm';

interface CreateProductMasterDataExportConfigurationPaneProps {}

export default function CreateProductMasterDataExportConfigurationPane({}: CreateProductMasterDataExportConfigurationPaneProps) {
  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);
  const {
    productMasterDataExportConfigurations,
    setProductMasterDataExportConfigurations,
    productMasterDataExportConfigurationsLoading,
  } = useContext(ProductMasterDataExportConfigurationContext);

  const [input, setInput] = useState<ProductMasterDataExportConfigurationInput>(
    new ProductMasterDataExportConfigurationInput(
      ProductMasterDataExportConfiguration.default(
        currentCompany.id,
        (currentCompany.settings.language || ISO_LanguageCode.EN) as TranslationLanguage,
      ),
    ),
  );

  const [create, { loading }] = useRequest(ProductMasterDataExportConfigurationRequest.create);

  const handleCreate = async (
    configuration: ProductMasterDataExportConfigurationInput,
    items: DraggableProductMasterDataExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<ProductMasterDataExportConfigurationField>(items);

    const result = await create({ data: [new ProductMasterDataExportConfigurationInput(configuration).forCreate()] });
    if (!result) return;

    const config = result[0];
    productMasterDataExportConfigurations.set(config.id, new ProductMasterDataExportConfiguration(config));
    setProductMasterDataExportConfigurations(new Map(productMasterDataExportConfigurations));
    navigate(Routing.productMasterData.settings.report.overview());
  };

  const footer = (
    configuration: ProductMasterDataExportConfigurationInput,
    items: DraggableProductMasterDataExportFieldIdentifier[],
  ) => {
    return (
      <div className='flex justify-end'>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={loading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.stock.products.settings.report.overview())}
          />
          <CreateButton
            loading={loading}
            onClick={() => handleCreate(configuration, items)}
            disabled={!configuration.filename || !configuration.name}
          />
        </div>
      </div>
    );
  };

  return (
    <ProductMasterDataExportConfigurationForm
      input={input}
      setInput={setInput}
      footer={footer}
      loading={productMasterDataExportConfigurationsLoading}
    />
  );
}
